import React, { useEffect, useMemo, useState } from "react";
import {
  api,
  ALL_PLATFORMS,
  PLATFORM_META,
  type Platform,
  type PlatformPlan,
  type ReelDeployment,
  type WorkspaceReelLean,
} from "../api";

type ToastFn = (kind: "success" | "error" | "info", message: string) => void;

type Props = {
  showToast: ToastFn;
};

export const Deploy: React.FC<Props> = ({ showToast }) => {
  const [reels, setReels] = useState<WorkspaceReelLean[]>([]);
  const [deployments, setDeployments] = useState<ReelDeployment[]>([]);
  const [selectedReelId, setSelectedReelId] = useState<string | null>(null);
  const [current, setCurrent] = useState<ReelDeployment | null>(null);
  const [loading, setLoading] = useState(false);
  const [dispatching, setDispatching] = useState(false);

  const load = async (): Promise<void> => {
    setLoading(true);
    try {
      const [ws, deps] = await Promise.all([api.workspaceList(), api.deploymentsList()]);
      setReels(ws);
      setDeployments(deps);
      const next = selectedReelId
        ? selectedReelId
        : ws.find((r) => r.status === "approved")?.id ?? ws[0]?.id ?? null;
      setSelectedReelId(next);
    } catch (e) {
      showToast("error", `Deployment-Load fehlgeschlagen: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  // Load the deployment plan for the selected reel.
  useEffect(() => {
    if (!selectedReelId) {
      setCurrent(null);
      return;
    }
    void (async () => {
      try {
        const dep = await api.deploymentsGet(selectedReelId);
        setCurrent(dep);
      } catch (e) {
        showToast("error", `Plan-Load fehlgeschlagen: ${(e as Error).message}`);
      }
    })();
  }, [selectedReelId, showToast]);

  const reelsById = useMemo(() => {
    const m = new Map<string, WorkspaceReelLean>();
    for (const r of reels) m.set(r.id, r);
    return m;
  }, [reels]);

  const onPlatformPatch = async (platform: Platform, patch: Partial<PlatformPlan>): Promise<void> => {
    if (!selectedReelId) return;
    try {
      const saved = await api.deploymentsSetPlatform(selectedReelId, platform, patch);
      setCurrent(saved);
      // refresh list view summary
      setDeployments((prev) => {
        const idx = prev.findIndex((p) => p.reelId === saved.reelId);
        if (idx < 0) return [...prev, saved];
        const next = [...prev];
        next[idx] = saved;
        return next;
      });
    } catch (e) {
      showToast("error", `Speichern fehlgeschlagen: ${(e as Error).message}`);
    }
  };

  const onDispatch = async (): Promise<void> => {
    if (!selectedReelId) return;
    setDispatching(true);
    try {
      const res = await api.deploymentsDispatch(selectedReelId);
      const sent = res.intents.length;
      showToast("success", `${sent} Plattformen auf "queued" gesetzt. Direkt-Posting kommt mit den API-Adaptern.`);
      const dep = await api.deploymentsGet(selectedReelId);
      setCurrent(dep);
    } catch (e) {
      showToast("error", `Dispatch fehlgeschlagen: ${(e as Error).message}`);
    } finally {
      setDispatching(false);
    }
  };

  const reel = selectedReelId ? reelsById.get(selectedReelId) ?? null : null;

  return (
    <div className="workspace">
      <div className="workspace-head">
        <div className="workspace-head-left">
          <h2>Deployment</h2>
          <p className="workspace-sub">
            Captions, Hashtags und Schedule pro Plattform — Felder werden persistiert, Direkt-Posting kommt
            mit den nativen Adaptern (Meta Graph, X v2, YouTube Data, TikTok Content Posting).
          </p>
        </div>
        <div className="workspace-head-actions">
          <button className="btn ghost" onClick={() => void load()} disabled={loading}>
            {loading ? "…" : "↻ Refresh"}
          </button>
          <button
            className="btn primary"
            onClick={() => void onDispatch()}
            disabled={!current || dispatching || Object.keys(current?.platforms ?? {}).length === 0}
          >
            {dispatching ? "…" : "🚀 Stub-Dispatch"}
          </button>
        </div>
      </div>

      <div
        style={{
          background: "rgba(245, 158, 11, 0.12)",
          border: "1px solid rgba(245, 158, 11, 0.4)",
          color: "#fcd34d",
          padding: "10px 14px",
          borderRadius: 12,
          marginBottom: 16,
          fontSize: 13,
        }}
      >
        ⚠️ <strong>Stub-Phase:</strong> Captions, Hashtags und Zeitpläne werden gespeichert. Der Stub-Dispatch
        liefert pro Plattform eine Share-Intent-URL (X, Facebook, Threads) bzw. setzt den Status auf{" "}
        <em>queued</em> — echtes Posting kommt mit OAuth + Provider-API in einer Folge-Iteration.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 16 }}>
        <aside style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {reels.length === 0 ? (
            <div style={{ opacity: 0.6 }}>Noch keine Reels im Workspace.</div>
          ) : (
            reels.map((r) => {
              const dep = deployments.find((d) => d.reelId === r.id);
              const platformCount = dep ? Object.keys(dep.platforms).length : 0;
              const sentCount = dep
                ? Object.values(dep.platforms).filter((p) => p && p.status === "sent").length
                : 0;
              const active = r.id === selectedReelId;
              return (
                <button
                  key={r.id}
                  onClick={() => setSelectedReelId(r.id)}
                  style={{
                    textAlign: "left",
                    background: active ? "rgba(59,130,246,0.18)" : "rgba(255,255,255,0.04)",
                    border: `1px solid ${active ? "#3b82f6" : "rgba(255,255,255,0.1)"}`,
                    borderRadius: 12,
                    padding: 12,
                    color: "inherit",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ fontSize: 12, opacity: 0.65, marginBottom: 4 }}>
                    {r.status} · {r.format}
                  </div>
                  <div style={{ fontWeight: 600 }}>{r.title}</div>
                  {platformCount > 0 ? (
                    <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
                      {platformCount} Plattformen {sentCount > 0 ? `· ${sentCount} sent` : ""}
                    </div>
                  ) : null}
                </button>
              );
            })
          )}
        </aside>

        {!reel ? (
          <div style={{ opacity: 0.6 }}>Wähle links ein Reel.</div>
        ) : (
          <section
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
              gap: 12,
            }}
          >
            {ALL_PLATFORMS.map((p) => {
              const plan = (current?.platforms ?? {})[p];
              return (
                <PlatformCard
                  key={p}
                  platform={p}
                  plan={plan}
                  reelTitle={reel.title}
                  onPatch={(patch) => void onPlatformPatch(p, patch)}
                />
              );
            })}
          </section>
        )}
      </div>
    </div>
  );
};

type CardProps = {
  platform: Platform;
  plan: PlatformPlan | undefined;
  reelTitle: string;
  onPatch: (patch: Partial<PlatformPlan>) => void;
};

const PlatformCard: React.FC<CardProps> = ({ platform, plan, reelTitle, onPatch }) => {
  const meta = PLATFORM_META[platform];
  const [caption, setCaption] = useState(plan?.caption ?? "");
  const [hashtags, setHashtags] = useState((plan?.hashtags ?? []).join(" "));
  const [scheduledAt, setScheduledAt] = useState(plan?.scheduledAt?.slice(0, 16) ?? "");
  const [enabled, setEnabled] = useState<boolean>(Boolean(plan));

  useEffect(() => {
    setCaption(plan?.caption ?? "");
    setHashtags((plan?.hashtags ?? []).join(" "));
    setScheduledAt(plan?.scheduledAt?.slice(0, 16) ?? "");
    setEnabled(Boolean(plan));
  }, [plan]);

  const save = (patch: Partial<PlatformPlan> = {}): void => {
    onPatch({
      status: plan?.status ?? "planned",
      caption,
      hashtags: hashtags
        .split(/\s+/)
        .map((h) => h.replace(/^#/, "").trim())
        .filter(Boolean),
      scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : undefined,
      ...patch,
    });
  };

  const onCopy = async (): Promise<void> => {
    const text = [caption, ...hashtags.split(/\s+/).filter(Boolean).map((h) => `#${h.replace(/^#/, "")}`)].join(" ");
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // ignore — older browsers
    }
  };

  return (
    <div
      style={{
        background: "rgba(255,255,255,0.04)",
        border: `1px solid ${enabled ? meta.color : "rgba(255,255,255,0.1)"}`,
        borderRadius: 12,
        padding: 12,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 24,
              height: 24,
              background: meta.color,
              color: "white",
              borderRadius: 6,
            }}
          >
            {meta.icon}
          </span>
          <strong>{meta.label}</strong>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => {
              const next = e.target.checked;
              setEnabled(next);
              if (next) {
                save({});
              }
            }}
          />
          Aktiv
        </label>
      </div>

      {enabled ? (
        <>
          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            onBlur={() => save()}
            placeholder={`Caption für ${meta.label}…`}
            rows={3}
            style={{ ...inputStyle, resize: "vertical" }}
          />
          <input
            value={hashtags}
            onChange={(e) => setHashtags(e.target.value)}
            onBlur={() => save()}
            placeholder="hashtags (mit oder ohne #)"
            style={inputStyle}
          />
          <label style={{ fontSize: 12, opacity: 0.8 }}>
            Veröffentlichung
            <input
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => {
                setScheduledAt(e.target.value);
                save({
                  scheduledAt: e.target.value ? new Date(e.target.value).toISOString() : undefined,
                });
              }}
              style={{ ...inputStyle, marginTop: 4 }}
            />
          </label>
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            <span
              style={{
                background: "rgba(0,0,0,0.3)",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 999,
                padding: "2px 8px",
                fontSize: 11,
                color: meta.color,
              }}
            >
              Status: {plan?.status ?? "planned"}
            </span>
            {plan?.error ? (
              <span style={{ color: "#ef4444", fontSize: 11 }}>{plan.error}</span>
            ) : null}
            <span style={{ flex: 1 }} />
            <button className="btn ghost sm" onClick={() => void onCopy()}>
              📋 Kopieren
            </button>
            {(platform === "x" || platform === "facebook" || platform === "threads") ? (
              <button
                className="btn ghost sm"
                onClick={() => {
                  const text = [caption, ...hashtags.split(/\s+/).filter(Boolean).map((h) => `#${h.replace(/^#/, "")}`)].join(" ");
                  const intent =
                    platform === "x"
                      ? `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`
                      : platform === "threads"
                        ? `https://www.threads.net/intent/post?text=${encodeURIComponent(text)}`
                        : `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent("https://example.com")}&quote=${encodeURIComponent(text)}`;
                  window.open(intent, "_blank", "noopener");
                }}
              >
                ↗ Native öffnen
              </button>
            ) : (
              <span style={{ fontSize: 11, opacity: 0.55 }} title="Native API folgt mit OAuth">
                Native API folgt
              </span>
            )}
          </div>
        </>
      ) : (
        <div style={{ opacity: 0.55, fontSize: 13 }}>
          Noch nicht für {meta.label} geplant. Aktivieren, um Caption, Hashtags und Schedule zu erfassen.
        </div>
      )}
      <div style={{ fontSize: 11, opacity: 0.5 }}>Reel: {reelTitle}</div>
    </div>
  );
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  background: "#0a0f17",
  color: "#e6edf6",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 8,
  padding: "6px 10px",
  fontSize: 13,
  fontFamily: "inherit",
};
